import type { LocationQueryRaw, RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import { nextTick } from 'vue'
import { i18n } from '@/i18n'
import { useA11yStore } from '@/stores/a11y'

/**
 * Titre de page, annonce vocale et déplacement du focus à chaque navigation.
 *
 * ─── Le problème ───
 *
 * Dans une application à page unique, changer d'écran ne recharge rien. Pour un
 * lecteur d'écran, il ne s'est donc RIEN passé : il continue de lire là où il
 * était, ou se tait. Et le focus reste sur le lien du menu qu'on vient
 * d'activer — la tabulation suivante continue dans le menu, pas dans le nouvel
 * écran. C'est le défaut d'accessibilité le plus répandu des applications Vue et
 * React, et il ne se voit jamais à la souris.
 *
 * Trois corrections, dans cet ordre :
 *
 *   1. `document.title` — c'est ce que la plupart des lecteurs d'écran énoncent
 *      au chargement d'une page, et ce qui nomme les onglets et l'historique du
 *      navigateur ;
 *   2. une annonce dans la région « live », parce que le changement de titre
 *      seul n'est PAS annoncé de façon fiable en navigation à page unique ;
 *   3. le focus déplacé sur `<main>`, pour que la tabulation reprenne dans le
 *      contenu.
 */

/** Titre de chaque route, en clé i18n. */
const TITRES: Record<string, string> = {
  login: 'auth.title',
  accueil: 'accueil.title',
  'superuser.organisations': 'nav.superuser.organisations',
  'superuser.plans': 'nav.superuser.plans',
  'superuser.services': 'nav.superuser.services',
  'superuser.apparence': 'nav.superuser.apparence',
  structure: 'nav.structure',
  developpeurs: 'dev.title',
  automatisations: 'automatisations.title',
  'not-found': 'notFound.title',
}

/**
 * Titre DYNAMIQUE d'une route dont le nom vient des données (une vue définie en
 * base) : `meta.titre(to)` le fournit, ou `null` s'il n'est pas encore connu.
 */
type TitreDynamique = (to: RouteLocationNormalized) => string | null

function nomDeLEcran(to: RouteLocationNormalized): string {
  const cle = typeof to.name === 'string' ? TITRES[to.name] : undefined
  if (cle !== undefined) return i18n.global.t(cle)
  const dynamique = to.meta.titre as TitreDynamique | undefined
  return typeof dynamique === 'function' ? (dynamique(to) ?? '') : ''
}

/**
 * Pose le titre du document. Exporté pour un écran dont le nom n'est connu
 * qu'APRÈS chargement (lien profond vers une vue, avant que la liste des vues
 * soit arrivée) : il complète le titre au lieu de laisser le seul nom de
 * l'application.
 */
export function definirTitreDocument(nomEcran: string): void {
  const nomApp = i18n.global.t('app.name')
  // Écran d'abord : dans un onglet réduit, seuls les premiers caractères sont
  // visibles, et c'est l'écran qui distingue les onglets — pas le nom de
  // l'application, identique partout.
  document.title = nomEcran === '' ? nomApp : `${nomEcran} · ${nomApp}`
}

/**
 * Navigation déclenchée par un CONTRÔLE DE LA PAGE qui doit garder le focus :
 * une barre d'onglets dont chaque onglet est une route. Le motif ARIA des
 * onglets veut que les flèches passent d'un onglet au suivant ; déplacer le
 * focus sur `<main>` à chaque flèche sortait l'utilisateur de la barre — il
 * devait y revenir à la tabulation pour atteindre l'onglet d'après.
 *
 * Le titre et l'annonce, eux, restent : le contenu a changé, et c'est
 * l'annonce qui le dit au lecteur d'écran.
 *
 * Drapeau MODULE posé le temps de CETTE navigation (`afterEach` s'exécute
 * avant que `push` ne se résolve) et retiré quoi qu'il arrive : une
 * navigation annulée ou refusée ne doit pas en épargner une autre.
 */
let focusConserve = false

export async function naviguerEnGardantLeFocus(router: Router, cible: RouteLocationRaw): Promise<void> {
  focusConserve = true
  try {
    await router.push(cible)
  } finally {
    focusConserve = false
  }
}

/**
 * Changement de la seule REQUÊTE d'un écran (`?mois=2026-11`,
 * `?affichage=kanban`) : on reste sur le même écran, rien n'est « chargé ».
 * Ni annonce « Page chargée », ni focus envoyé sur `<main>` — l'utilisateur
 * qui vient de cliquer « Mois suivant » le perdrait, et devrait retraverser
 * toute la page pour cliquer encore. L'écran annonce lui-même ce qui change.
 *
 * `replace` : l'historique ne se remplit pas d'un pas par mois parcouru.
 * Même drapeau MODULE que `naviguerEnGardantLeFocus`, retiré quoi qu'il arrive.
 */
let requeteSeule = false

export async function remplacerRequete(router: Router, query: LocationQueryRaw): Promise<void> {
  requeteSeule = true
  try {
    await router.replace({ query })
  } finally {
    requeteSeule = false
  }
}

export function installerAnnoncesRoute(router: Router): void {
  router.afterEach((to, depuis) => {
    const { t } = i18n.global
    const nomEcran = nomDeLEcran(to)
    definirTitreDocument(nomEcran)

    // Première navigation (`depuis.name` absent) : le navigateur vient de
    // charger le document, il l'annonce lui-même. Annoncer en plus donnerait un
    // doublon, et déplacer le focus retirerait à l'utilisateur son point de
    // départ naturel — dont le lien d'évitement.
    if (depuis.name === undefined) return
    if (requeteSeule) return

    const a11y = useA11yStore()
    if (nomEcran !== '') a11y.annoncer(t('a11y.announce.navigated', { page: nomEcran }))

    if (focusConserve) return

    // Après le rendu du nouvel écran : avant, l'élément visé peut encore être
    // celui de l'écran précédent, voire absent pendant le chargement paresseux
    // du fragment.
    void nextTick(() => {
      const principal = document.getElementById('contenu-principal')
      // `preventScroll` : le comportement de défilement est déjà géré par
      // `scrollBehavior` du routeur, qui sait restituer la position au retour
      // arrière. Sans ce drapeau, le focus écraserait cette restitution.
      principal?.focus({ preventScroll: true })
    })
  })
}
